/***************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import uuid from 'uuid/v4';

const IS_INTERNAL = !!window.uxpHost;
const msgQueue = [];
let queueInterval;

/**
 * Constructs and sends messages
 * @param {String} command - command to execute
 * @param {Object} payload - payload for event
 * @param {String} msgGuid - guid for message being sent
 * @returns {undefined}
 */
function sendHelper(command, payload, msgGuid) {
  msgQueue.push({
    command,
    msgGuid,
    payload
  });

  if (!queueInterval) {
    queueInterval = setInterval(() => {
      if (msgQueue.length) {
        const nextMsg = msgQueue.shift();
        if (!IS_INTERNAL) {
          let webview = document.getElementById('commentsWebview');
          if (webview.tagName === 'IFRAME') {
            webview = webview.contentWindow;
          }
          webview.postMessage(nextMsg);
        } else {
          window.uxpHost.postMessage(nextMsg);
        }
      } else {
        clearInterval(queueInterval);
        queueInterval = undefined;
      }
    }, 10);
  }
}

/**
 * Class for an isomorphic message bridge to handle communicate across UXP webview
 */
class IsomorphicMessageBridge {
  /**
   * Constructor for IsomorphicMessageBridge
   * @returns {undefined}
   */
  constructor() {
    if (!IsomorphicMessageBridge.instance) {
      IsomorphicMessageBridge.instance = this;
    }

    this.listenerMap = {};
    this.subscriptionMap = {};

    this.subscribe = this.subscribe.bind(this);
    this.send = this.send.bind(this);

    window.addEventListener('message', evt => this._handleGeneralMessage(evt));

    return IsomorphicMessageBridge.instance;
  }

  /**
   * Private method for handling message events for subscriptions
   * @param {Object} evt - payload for event
   * @returns {undefined}
   */
  _handleGeneralMessage(evt) {
    const { command, payload, msgGuid } = evt.data;
    if (this.subscriptionMap[command]) {
      this.subscriptionMap[command].forEach(cb => {
        cb(msgGuid, payload);
      });
    }
  }

  /**
   * Private method for handling message events
   * @param {Object} evt - payload for event
   * @param {Function} resolve - method to resolve promise with response
   * @param {String} matchingMsgGuid - guid for associated message
   * @returns {undefined}
   */
  _handleMessage(evt, resolve, matchingMsgGuid) {
    const { payload, msgGuid } = evt.data;
    if (msgGuid === matchingMsgGuid) {
      resolve(payload);
      window.removeEventListener('message', this.listenerMap[msgGuid]);
      delete this.listenerMap[msgGuid];
    }
  }

  /**
   * Method for sending events across UXP webview
   * @param {String} command - command that is being sent
   * @param {Object} payload - payload to be sent with message
   * @param {String} originatingMsgGuid - optional guid if this message is in response to a previous message
   * @param {Boolean} sendResponse - optional boolean that should be passed if no response is required
   * @returns {Promise} promise to be resolved once associated data has been fetched
   */
  send(command, payload, originatingMsgGuid, sendResponse = true) {
    const msgGuid = originatingMsgGuid || uuid();
    let responsePromise;

    if (sendResponse) {
      responsePromise = new Promise(resolve => {
        this.listenerMap[msgGuid] = evt => this._handleMessage(evt, resolve, msgGuid);
        window.addEventListener('message', this.listenerMap[msgGuid]);
      });
    }

    sendHelper(command, payload, msgGuid);

    return responsePromise;
  }

  /**
   * Method for sending events across UXP webview
   * @param {String} command - command that is being subscribed to
   * @param {Function} cb - callback to be called when messages of associated command are recieved
   * @returns {undefined}
   */
  subscribe(command, cb) {
    if (!this.subscriptionMap[command]) {
      this.subscriptionMap[command] = [];
    }
    this.subscriptionMap[command].push(cb);
  }
}

export default new IsomorphicMessageBridge();
